import { type Component } from 'vue'
import {
  createWebHistory,
  createMemoryHistory,
  createRouter as _createRouter,
  type RouteLocationNormalizedLoadedGeneric,
} from 'vue-router'
import { getBrandModelId } from '@/helpers/product'
import { useProductsStore } from '@/store/modules/products'
import { usePagesStore } from '@/store/modules/pages'
import { productCategoriesMap } from '@/composables/useProductList'
import { createSlug } from '@/helpers/util/index'

const printerTypeRegex = '(multifunctionals|print-only|production|plotters)'
const scannerTypeRegex = '(personal|workgroup|departmental|production)'

type ProductCategory =
  | 'multifunctional-printer'
  | 'office-printer'
  | 'production-printer'
  | 'production-scanner'
  | 'plotter'
  | 'scanner'
  | 'finisher'

interface RouteMeta {
  fetchPage?: boolean
  fetchProduct?: boolean
  fetchProductList?: boolean
  fetchScanners?: boolean
  fetchFinishers?: boolean
  productCategory?: ProductCategory
  isCategory?: boolean
  productType?: string
  sortOn?: string
  landingPage?: boolean
}

interface Route {
  path: string
  name: string
  component: () => Promise<Component>
  meta?: RouteMeta
  validate?: (params: { type: string }) => boolean
  beforeEnter?: (
    to: RouteLocationNormalizedLoadedGeneric,
    from: RouteLocationNormalizedLoadedGeneric
  ) => Promise<unknown> | void
}

// async function fetchData(route: RouteLocationNormalizedLoadedGeneric) {
//   const brand = route.params.brand
//   const model = route.params.model
//   const type = route.params.type
//   const modelId = getBrandModelId(brand, model)
//   const request = {
//     brand,
//     model,
//     type,
//     modelId,
//   }
//   console.log('Fetching product data for', request)
//   // If scanner product fethc scanner data
//   // if (route.meta.fetchScanners) {
//   //   return await productStore.FETCH_SCANNER(request, route)
//   // }

//   // return await productStore.FETCH_PRINTER(request, route)
// }

// onBeforeRouteUpdate(async (to) => {
//   // console.log('beforeRouteUpdate', to)
//   const brand = to.params.brand
//   const model = to.params.model
//   const modelId = getBrandModelId(brand, model)
//   console.log('Updating product data for', brand, model, modelId)
//   if (!productStore.viewSingleProduct) {
//     // No product data, fetch it
//     await fetchData(to)
//   } else {
//     if (!productStore.viewSingleProduct || productStore.viewSingleProduct.modelId !== modelId) {
//       // No match
//       await fetchData(to)
//     }
//   }
// })

const productPageBeforeEnter = async (to: RouteLocationNormalizedLoadedGeneric) => {
  const productStore = useProductsStore()
  const { brand, model, type } = to.params
  const modelId = getBrandModelId(brand, model)
  const request = {
    brand,
    model,
    type,
    modelId,
  }

  try {
    return await productStore.FETCH_PRODUCT(request)
  } catch (error) {
    console.error('Error fetching product data:', error)

    return {
      name: 'NotFound',
    }
  }
}

const productLisPageBeforeEnter = async (to: RouteLocationNormalizedLoadedGeneric) => {
  try {
    const productsStore = useProductsStore()
    const pagesStore = usePagesStore()

    const deviceBaseType = to.meta.productCategory as ProductCategory
    const apiCategory = productCategoriesMap[deviceBaseType.value] || deviceBaseType.value

    await Promise.all([
      productsStore.getProductsByCategory(deviceBaseType, apiCategory),
      pagesStore.fetchPage(to),
    ])
  } catch (error) {
    console.error('Error fetching product list data:', error)
  }
}

const routes: Route[] = [
  {
    path: '/',
    name: 'Home',
    component: () => import('../views/Home.vue'),
    meta: {
      fetchPage: true,
    },
    beforeEnter: async (to) => {
      const store = usePagesStore()
      const slug = 'home'

      // Fetch data only if it doesn't exist.
      // This logic is now centralized in the router.
      if (!store.pageContent[slug]) {
        await store.fetchPage(to)
      }
    },
  },
  {
    path: '/contact-us',
    name: 'ContactUs',
    component: () => import('../views/Contact.vue'),
    meta: {
      fetchPage: true,
    },
    beforeEnter: async (to) => {
      const store = usePagesStore()
      const slug = createSlug(to.path)

      // Fetch data only if it doesn't exist.
      // This logic is now centralized in the router.
      if (!store.pageContent[slug]) {
        await store.fetchPage(to)
      }
    },
  },
  {
    path: '/customer-support',
    name: 'CustomerSupport',
    component: () => import('../views/Support.vue'),
    meta: {
      fetchPage: true,
    },
    beforeEnter: async (to) => {
      const store = usePagesStore()
      const slug = createSlug(to.path)

      // Fetch data only if it doesn't exist.
      // This logic is now centralized in the router.
      if (!store.pageContent[slug]) {
        await store.fetchPage(to)
      }
    },
  },
  // Keep type for printers as it more specifically defines a different kind of printer
  {
    path: `/printers/:type${printerTypeRegex}/:brand/:model`,
    name: 'PrinterProduct',
    component: () => import('../views/ProductPage.vue'),
    beforeEnter: productPageBeforeEnter,
    meta: {
      fetchProduct: true,
    },
    validate: (params: { type: string }) => {
      const validTypes = ['multifunctionals', 'print-only', 'production', 'plotters']
      return validTypes.includes(params.type)
    },
  },
  // Removed type from scanner product page
  {
    path: `/scanners/production/:brand/:model`,
    name: 'ProductionScannerProduct',
    component: () => import('../views/ProductPage.vue'),
    beforeEnter: productPageBeforeEnter,
    meta: {
      fetchProduct: true,
      fetchScanners: true,
    },
    // validate: (params) => {
    //   const validTypes = ['personal', 'workgroup', 'departmental', 'production', 'all']
    //   return validTypes.includes(params.type)
    // },
  },
  // Removed type from scanner product page
  {
    path: `/scanners/:brand/:model`,
    name: 'ScannerProduct',
    component: () => import('../views/ProductPage.vue'),
    beforeEnter: productPageBeforeEnter,
    meta: {
      fetchProduct: true,
      fetchScanners: true,
    },
    // validate: (params) => {
    //   const validTypes = ['personal', 'workgroup', 'departmental', 'production', 'all']
    //   return validTypes.includes(params.type)
    // },
  },
  // Add finisher type
  {
    path: `/finishers/:brand/:model`,
    name: 'FinisherProduct',
    component: () => import('../views/ProductPage.vue'),
    beforeEnter: productPageBeforeEnter,
    meta: {
      fetchProduct: true,
      fetchFinishers: true,
    },
  },

  {
    path: '/printers/multifunctionals',
    name: 'MultifunctionalPrinterList',
    // props: route => ({ page: route.query.page }),
    component: () => import('../views/ProductList.vue'),
    // beforeEnter: productLisPageBeforeEnter,
    meta: {
      productCategory: 'multifunctional-printer',
      fetchPage: true,
      fetchProductList: true,
    },
  },
  {
    path: '/printers/print-only',
    name: 'PrintOnlyPrinterList',
    // props: route => ({ page: route.query.page }),
    component: () => import('../views/ProductList.vue'),
    // beforeEnter: productLisPageBeforeEnter,
    meta: {
      productCategory: 'office-printer',
      fetchPage: true,
      fetchProductList: true,
    },
  },
  {
    path: '/printers/production',
    name: 'ProductionPrinterList',
    // props: route => ({ page: route.query.page }),
    component: () => import('../views/ProductList.vue'),
    // beforeEnter: productLisPageBeforeEnter,
    meta: {
      productCategory: 'production-printer',
      fetchPage: true,
      fetchProductList: true,
    },
  },
  {
    path: '/printers/plotters',
    name: 'PlottersList',
    // props: route => ({ page: route.query.page }),
    component: () => import('../views/ProductList.vue'),
    // beforeEnter: productLisPageBeforeEnter,
    meta: {
      productCategory: 'plotter',
      fetchPage: true,
      fetchProductList: true,
    },
  },
  {
    path: `/scanners`,
    name: 'ScannerList',
    // props: route => ({ page: route.query.page }),
    component: () => import('../views/ProductList.vue'),
    // beforeEnter: productLisPageBeforeEnter,
    meta: {
      productCategory: 'scanner',
      fetchPage: true,
      fetchProductList: true,
      fetchScanners: true,
    },
  },
  {
    path: `/finishers`,
    name: 'FinisherList',
    // props: route => ({ page: route.query.page }),
    component: () => import('../views/ProductList.vue'),
    // beforeEnter: productLisPageBeforeEnter,
    meta: {
      productCategory: 'finisher',
      fetchPage: true,
      fetchProductList: true,
      fetchFinishers: true,
    },
  },
  {
    path: `/printers/:category${printerTypeRegex}`,
    name: 'PrinterCategoryList',
    // props: route => ({ page: route.query.page }),
    component: () => import('../views/ProductList.vue'),
    // beforeEnter: productLisPageBeforeEnter,
    meta: {
      productCategory: 'office-printer',
      fetchPage: true,
      fetchProductList: true,
    },
  },

  {
    path: `/scanners/production`,
    name: 'ProductionScannerCategoryList',
    // props: route => ({ page: route.query.page }),
    component: () => import('../views/ProductList.vue'),
    // beforeEnter: productLisPageBeforeEnter,
    meta: {
      productCategory: 'production-scanner',
      fetchPage: true,
      fetchProductList: true,
      fetchScanners: true,
    },
  },
  {
    path: `/scanners/:category${scannerTypeRegex}`,
    name: 'ScannerCategoryList',
    // props: route => ({ page: route.query.page }),
    component: () => import('../views/ProductList.vue'),
    // beforeEnter: productLisPageBeforeEnter,
    meta: {
      productCategory: 'scanner',
      fetchPage: true,
      fetchProductList: true,
      fetchScanners: true,
    },
  },

  {
    path: `/finishers/:category`,
    name: 'FinisherCategoryList',
    // props: route => ({ page: route.query.page }),
    component: () => import('../views/ProductList.vue'),
    // beforeEnter: productLisPageBeforeEnter,
    meta: {
      productCategory: 'finisher',
      fetchPage: true,
      fetchProductList: true,
      fetchFinishers: true,
    },
  },
  {
    path: `/printers/multifunctionals/:subcategory`,
    name: 'MultifunctionalPrinterSpecificCategoryList',
    // props: route => ({ page: route.query.page }),
    component: () => import('../views/ProductList.vue'),
    // beforeEnter: productLisPageBeforeEnter,
    meta: {
      productCategory: 'multifunctional-printer',
      fetchPage: true,
      fetchProductList: true,
      isCategory: true,
    },
  },
  {
    path: `/printers/print-only/:subcategory`,
    name: 'PrintOnlyPrinterSpecificCategoryList',
    // props: route => ({ page: route.query.page }),
    component: () => import('../views/ProductList.vue'),
    // beforeEnter: productLisPageBeforeEnter,
    meta: {
      productCategory: 'office-printer',
      fetchPage: true,
      fetchProductList: true,
      isCategory: true,
    },
  },
  {
    path: `/printers/plotters/:subcategory`,
    name: 'PlottersSpecificCategoryList',
    // props: route => ({ page: route.query.page }),
    component: () => import('../views/ProductList.vue'),
    // beforeEnter: productLisPageBeforeEnter,
    meta: {
      productCategory: 'plotter',
      fetchPage: true,
      fetchProductList: true,
      isCategory: true,
    },
  },
  {
    path: `/printers/production/:subcategory`,
    name: 'ProductionPrinterSpecificCategoryList',
    // props: route => ({ page: route.query.page }),
    component: () => import('../views/ProductList.vue'),
    // beforeEnter: productLisPageBeforeEnter,
    meta: {
      productCategory: 'production-printer',
      fetchPage: true,
      fetchProductList: true,
      isCategory: true,
    },
  },
  {
    path: '/discover-print-savings',
    name: 'ServiceChecker',
    component: () => import('../views/ServiceChecker.vue'),
    meta: {
      fetchPage: true,
    },
  },
  {
    path: '/services/:slug',
    name: 'service-page',
    component: () => import('../views/Service.vue'),
    meta: {
      fetchPage: true,
    },
    beforeEnter: async (to) => {
      const store = usePagesStore()
      const slug = createSlug(to.path)

      // Fetch data only if it doesn't exist.
      // This logic is now centralized in the router.
      if (!store.pageContent[slug]) {
        await store.fetchPage(to)
      }
    },
  },
  // Landing pages below, these are not part of the main navigation
  // Probably need a longer term solution, but we can hardcode the first one.
  {
    path: '/photocopiers',
    name: 'PhotocopiersLandingpage',
    component: () => import('../views/landing-pages/ProductKeyword.vue'),
    meta: {
      productCategory: 'multifunctional-printer',
      landingPage: true,
      fetchPage: true,
      fetchProductList: true,
    },
  },
  {
    path: '/commercial-printers',
    name: 'CommercialPrintersLandingpage',
    component: () => import('../views/landing-pages/ProductKeyword.vue'),
    meta: {
      productCategory: 'production-printer',
      landingPage: true,
      fetchPage: true,
      fetchProductList: true,
    },
  },
  {
    path: '/office-printers',
    name: 'OfficePrintersLandingpage',
    component: () => import('../views/landing-pages/ProductKeyword.vue'),
    meta: {
      productCategory: 'multifunctional-printer',
      sortOn: 'colour',
      landingPage: true,
      fetchPage: true,
      fetchProductList: true,
    },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound', // <-- ADD THIS LINE
    component: () => import('../views/error-pages/NotFound.vue'),
  },
]

export const createRouter = () =>
  _createRouter({
    routes,
    history: import.meta.env.SSR
      ? createMemoryHistory()
      : createWebHistory(import.meta.env.BASE_URL),
    scrollBehavior(to, from, savedPosition) {
      // Check if we are in a browser environment
      if (typeof window === 'undefined') {
        return
      }

      if (savedPosition && savedPosition.left !== 0 && savedPosition.top !== 0) {
        // savedPosition is only available for popstate navigations.
        return savedPosition
      } else {
        const position: {
          el?: string
          top?: number
          left?: number
          behavior?: 'smooth' | 'auto'
          selector?: string
        } = {}
        // scroll to anchor by returning the selector
        if (to.hash) {
          position.selector = to.hash
          if (document.querySelector(to.hash)) {
            return { el: to.hash }
          }

          // if the returned position is falsy or an empty object,
          // will retain current scroll position.
          return false
        } else {
          return { top: 0 }
        }
      }
    },
  })
